import { useCallback, useEffect, useState } from "react";
import { Search, Minus, Plus, ScrollText } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import InlineNumberEditor from "../../components/InlineNumberEditor";
import PropagationBadge from "../../components/PropagationBadge";
import { toast } from "sonner";

function StockAdjustPopover({ row, onAdjust, kind }) {
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState(1);
  const [note, setNote] = useState("");
  const isAdd = kind === "add";
  const submit = async () => {
    const n = Math.max(1, Number(amt) || 0);
    await onAdjust(isAdd ? n : -n, note);
    setOpen(false); setAmt(1); setNote("");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`stock-${kind}-${row.size_variant_id}`}
          className={`h-8 px-2 inline-flex items-center gap-1 rounded-md border text-xs shrink-0 ${
            isAdd ? "border-[#2D6A4F] text-[#1B4332] hover:bg-[#E9F5EC]"
                  : "border-[#C94A4A] text-[#C94A4A] hover:bg-[#FDF3F3]"
          }`}
        >
          {isAdd ? <Plus size={13} /> : <Minus size={13} />}
          {isAdd ? "Add" : "Reduce"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 space-y-2" align="center">
        <div className="text-xs db-h font-semibold uppercase text-[#5C665A]">
          {isAdd ? "Add stock" : "Reduce stock"} — current {row.stock_qty}
        </div>
        <Input type="number" min={1} value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Amount" data-testid={`stock-${kind}-amt-${row.size_variant_id}`} />
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" data-testid={`stock-${kind}-note-${row.size_variant_id}`} />
        {!isAdd && Number(amt) > row.stock_qty && (
          <div className="text-[11px] text-[#C94A4A]">Will clip at 0.</div>
        )}
        <Button onClick={submit} className={`w-full h-9 ${isAdd ? "bg-[#2D6A4F] hover:bg-[#1B4332]" : "bg-[#C94A4A] hover:bg-[#A63838]"} text-white`} data-testid={`stock-${kind}-confirm-${row.size_variant_id}`}>
          Confirm {isAdd ? "add" : "reduce"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export default function StockView() {
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [low, setLow] = useState(params.get("low") === "1");
  const [categoryId, setCategoryId] = useState("");
  const [sizeId, setSizeId] = useState("");
  const [categories, setCategories] = useState([]);
  const [sizeOpts, setSizeOpts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/size-options").then((r) => setSizeOpts(r.data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (low) p.set("low_stock", "true");
    if (categoryId) p.set("category_id", categoryId);
    if (sizeId) p.set("size_id", sizeId);
    try { const { data } = await api.get(`/admin/stock?${p.toString()}`); setRows(data); }
    finally { setLoading(false); }
  }, [q, low, categoryId, sizeId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (low) p.set("low", "1");
    setParams(p, { replace: true });
  }, [low, setParams]);

  const clearFilters = () => { setQ(""); setLow(false); setCategoryId(""); setSizeId(""); };
  const anyFilter = !!q || low || !!categoryId || !!sizeId;

  const adjust = async (row, delta, note) => {
    try {
      const { data } = await api.post(`/size-variants/${row.size_variant_id}/stock/adjust`, { delta, note: note || null });
      setRows((prev) => prev.map((r) => r.size_variant_id === row.size_variant_id ? { ...r, stock_qty: data.new_qty } : r));
      if (data.clipped_at_zero) toast.warning("Reduction was clipped at zero");
      else toast.success(`Stock ${delta > 0 ? "increased" : "reduced"} by ${Math.abs(delta)}`);
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const setExact = async (row, value) => {
    const newQty = Math.max(0, Number(value) || 0);
    if (newQty === row.stock_qty) return;
    try {
      const { data } = await api.patch(`/size-variants/${row.size_variant_id}/stock`, { stock_qty: newQty, note: "direct set" });
      setRows((prev) => prev.map((r) => r.size_variant_id === row.size_variant_id ? { ...r, stock_qty: data.new_qty } : r));
      toast.success("Stock updated");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed"));
      throw err;
    }
  };

  return (
    <>
      <PageHeader
        title="Stock" subtitle="Adjust stock across every size variant. Every change is logged." testid="page-stock"
        actions={<Link to="/admin/stock-logs" className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[#E2E4E0] text-sm hover:bg-[#F9F9F8]"><ScrollText size={14} /> View log</Link>}
      />
      <div className="p-4 sm:p-6 max-w-6xl">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-2 top-2.5 text-[#5C665A]" />
            <Input data-testid="stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product or SKU…" className="pl-7 h-9 w-full" />
          </div>
          <Select value={categoryId || "__all__"} onValueChange={(v) => setCategoryId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-full sm:w-40" data-testid="stock-filter-category"><SelectValue placeholder="Genus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All genera</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sizeId || "__all__"} onValueChange={(v) => setSizeId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-full sm:w-36" data-testid="stock-filter-size"><SelectValue placeholder="Size" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sizes</SelectItem>
              {sizeOpts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer h-9 px-2">
            <Checkbox data-testid="stock-low-filter" checked={low} onCheckedChange={(v) => setLow(!!v)} />
            Low stock (≤5)
          </label>
          {anyFilter && (
            <button onClick={clearFilters} className="text-xs text-[#5C665A] hover:text-[#1A1D1A]" data-testid="stock-clear-filters">Clear</button>
          )}
          <div className="sm:ml-auto text-xs text-[#5C665A]" data-testid="stock-count">
            {rows.length} variant{rows.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="text-left px-3 py-2 db-h font-semibold">Product</th>
                <th className="text-left px-3 py-2 db-h font-semibold w-32">Genus</th>
                <th className="text-left px-3 py-2 db-h font-semibold w-40">Propagation</th>
                <th className="text-left px-3 py-2 db-h font-semibold w-24">Size</th>
                <th className="text-left px-3 py-2 db-h font-semibold">SKU</th>
                <th className="text-center px-3 py-2 db-h font-semibold w-64">Stock</th>
                <th className="text-center px-3 py-2 db-h font-semibold w-32">Display</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#5C665A]" data-testid="stock-loading">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#5C665A]" data-testid="stock-empty">No variants.</td></tr>}
              {rows.map((r) => (
                <tr key={r.size_variant_id} className="border-t border-[#E2E4E0] db-row" data-testid={`stock-row-${r.size_variant_id}`}>
                  <td className="px-3 py-2 font-medium">
                    <Link to={`/admin/products/${r.product_id}`} className="hover:underline break-words" data-testid={`stock-product-link-${r.size_variant_id}`}>{r.product_name}</Link>
                    {r.product_status === "draft" && <span className="ml-2 text-[10px] text-[#5C665A] uppercase">draft</span>}
                    {r.product_status === "archived" && <span className="ml-2 text-[10px] text-[#5C665A] uppercase">archived</span>}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent" data-testid={`stock-category-${r.size_variant_id}`}>
                      {r.category_name}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <PropagationCell row={r} onSaved={(val) => setRows((prev) => prev.map((x) => x.product_id === r.product_id ? { ...x, propagation_type: val } : x))} />
                  </td>
                  <td className="px-3 py-2 text-[#5C665A]" data-testid={`stock-size-${r.size_variant_id}`}>
                    {r.size_name}
                    {r.size_note && <span className="ml-1 text-[11px] text-[#5C665A]/80" data-testid={`stock-size-note-${r.size_variant_id}`}>({r.size_note})</span>}
                  </td>
                  <td className="px-3 py-2 db-mono text-xs">{r.sku}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1.5 flex-nowrap whitespace-nowrap">
                      <StockAdjustPopover row={r} onAdjust={(delta, note) => adjust(r, delta, note)} kind="add" />
                      <InlineNumberEditor
                        value={r.stock_qty}
                        step="1"
                        min={0}
                        onCommit={(val) => setExact(r, val)}
                        inputClassName={`db-inline h-8 w-16 text-center db-mono shrink-0 ${r.stock_qty <= 5 ? "text-[#C94A4A]" : ""}`}
                        testid={`stock-qty-${r.size_variant_id}`}
                        ariaLabel="Set exact stock"
                      />
                      <StockAdjustPopover row={r} onAdjust={(delta, note) => adjust(r, delta, note)} kind="reduce" />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <DisplayQtyCell row={r} onSaved={load} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </>
  );
}

/** Inline propagation-type quick setter (None / Seed Derived / Clone).
 *  Cycles through the three states on click. Since propagation is a
 *  product-level attribute, saving affects every row for the same product;
 *  parent reloads to reflect. */
function PropagationCell({ row, onSaved }) {
  const [saving, setSaving] = useState(false);
  const cycle = { none: "seed_derived", seed_derived: "clone", clone: "none" };
  const current = row.propagation_type || "none";

  const set = async (next) => {
    if (saving) return;
    setSaving(true);
    const payload = { propagation_type: next === "none" ? null : next };
    try {
      await api.patch(`/products/${row.product_id}`, payload);
      onSaved(next === "none" ? null : next);
      toast.success(next === "none" ? "Tag cleared" : `Tagged as ${next === "seed_derived" ? "Seed Derived" : "Clone"}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Save failed"));
    } finally { setSaving(false); }
  };

  const cycleNext = () => set(cycle[current]);

  if (current === "none") {
    return (
      <button
        type="button"
        onClick={cycleNext}
        disabled={saving}
        title="Click to tag propagation — none → seed derived → clone → none"
        className="text-[11px] text-[#5C665A] hover:text-[#1B4332] hover:bg-[#F1F5F1] rounded px-2 h-6"
        data-testid={`stock-propagation-${row.size_variant_id}`}
      >
        + tag
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={cycleNext}
      disabled={saving}
      title="Click to change — seed derived → clone → none"
      className="inline-flex items-center focus:outline-none"
      data-testid={`stock-propagation-${row.size_variant_id}`}
    >
      <PropagationBadge type={current} size="xs" />
    </button>
  );
}

/** Inline banded-display override editor for the Stock admin table.
 *  Explicit-commit: type → ✓/✗ appear; Enter saves; Escape/Blur cancels.
 *  Empty string clears the override. */
function DisplayQtyCell({ row, onSaved }) {
  const currentShown = row.display_qty;
  const isOverride = row.display_qty_override != null;
  const savedValue = row.display_qty_override ?? "";

  const commit = async (raw) => {
    const trimmed = String(raw).trim();
    if (trimmed !== "" && (!Number.isFinite(Number(trimmed)) || Number(trimmed) < 1)) {
      toast.error("Display quantity must be at least 1 (leave empty to clear)");
      throw new Error("invalid");
    }
    const payload = trimmed === "" ? { display_qty_override: null } : { display_qty_override: Number(trimmed) };
    try {
      await api.patch(`/size-variants/${row.size_variant_id}/display-qty`, payload);
      toast.success(trimmed === "" ? "Override cleared" : "Display override set");
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, "Save failed"));
      throw err;
    }
  };

  return (
    <div className="inline-flex items-center gap-1" title="Banded display override — leave empty to use the auto band">
      <InlineNumberEditor
        value={savedValue}
        step="1"
        min={1}
        onCommit={commit}
        inputClassName={`db-inline h-8 w-16 text-center db-mono ${isOverride ? "border-[#2D6A4F] font-semibold" : ""}`}
        testid={`stock-display-override-${row.size_variant_id}`}
        ariaLabel={`Display override (auto: ${currentShown ?? "—"})`}
      />
    </div>
  );
}
